'use client'

import { Check, X, Minus, Clock, Shield } from 'lucide-react'

interface BotAccess {
  bot: string
  category: 'search' | 'ai-browse' | 'ai-train' | 'seo'
  allowed: boolean
  robotsTxt: 'allowed' | 'blocked' | 'no-rule'
  httpStatus: number
  responseTime: number
  xRobotsTag: string | null
}

interface BotAccessGridProps {
  bots: BotAccess[]
}

const CATEGORY_LABELS: Record<string, string> = {
  search: 'Search Engines',
  'ai-browse': 'AI Retrieval & General Crawlers',
  'ai-train': 'AI Training',
  seo: 'SEO Tools',
}

const CATEGORY_COLORS: Record<string, string> = {
  search: '#008dcc',
  'ai-browse': '#8b5cf6',
  'ai-train': '#f97316',
  seo: '#22c55e',
}

function StatusIcon({
  allowed,
  robotsTxt,
}: {
  allowed: boolean
  robotsTxt: string
}) {
  if (allowed) {
    return (
      <div
        className='w-7 h-7 rounded-full flex items-center justify-center'
        style={{ backgroundColor: '#22c55e20' }}
      >
        <Check size={16} style={{ color: '#22c55e' }} />
      </div>
    )
  }
  if (robotsTxt === 'no-rule') {
    return (
      <div
        className='w-7 h-7 rounded-full flex items-center justify-center'
        style={{ backgroundColor: '#FDC30420' }}
      >
        <Minus size={16} style={{ color: '#FDC304' }} />
      </div>
    )
  }
  return (
    <div
      className='w-7 h-7 rounded-full flex items-center justify-center'
      style={{ backgroundColor: '#F90B8A20' }}
    >
      <X size={16} style={{ color: '#F90B8A' }} />
    </div>
  )
}

function RobotsBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    allowed: { bg: '#22c55e15', text: '#22c55e' },
    blocked: { bg: '#F90B8A15', text: '#F90B8A' },
    'no-rule': { bg: '#FDC30415', text: '#FDC304' },
  }
  const c = colors[status] || colors['no-rule']
  return (
    <span
      className='text-[10px] font-medium rounded-full px-2 py-0.5'
      style={{ backgroundColor: c.bg, color: c.text }}
    >
      {status === 'no-rule' ? 'no rule' : status}
    </span>
  )
}

export default function BotAccessGrid({ bots }: BotAccessGridProps) {
  const categories = ['search', 'ai-browse', 'ai-train', 'seo'] as const

  return (
    <div className='space-y-6'>
      {categories.map(cat => {
        const catBots = bots.filter(b => b.category === cat)
        if (catBots.length === 0) return null
        const color = CATEGORY_COLORS[cat]

        return (
          <div key={cat}>
            <div className='flex items-center gap-2 mb-3'>
              <span
                className='h-2 w-2 rounded-full'
                style={{ backgroundColor: color }}
              />
              <span className='text-sm font-semibold text-foreground'>
                {CATEGORY_LABELS[cat]}
              </span>
              <span className='text-xs text-muted-foreground'>
                ({catBots.filter(b => b.allowed).length}/{catBots.length}{' '}
                accessible)
              </span>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
              {catBots.map(bot => (
                <div
                  key={bot.bot}
                  className='bg-background border border-border/60 rounded-xl p-3.5 flex items-center gap-3'
                >
                  <StatusIcon allowed={bot.allowed} robotsTxt={bot.robotsTxt} />
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-foreground truncate'>
                        {bot.bot}
                      </span>
                      <RobotsBadge status={bot.robotsTxt} />
                    </div>
                    {bot.bot === 'GoogleOther' && (
                      <p className='text-[11px] text-muted-foreground'>
                        General-purpose crawler · diagnostic only
                      </p>
                    )}
                    <div className='flex items-center gap-3 mt-0.5'>
                      <span className='text-[11px] text-muted-foreground'>
                        HTTP {bot.httpStatus || 'N/A'}
                      </span>
                      {bot.responseTime > 0 && (
                        <span className='text-[11px] text-muted-foreground flex items-center gap-0.5'>
                          <Clock size={10} />
                          {bot.responseTime}ms
                        </span>
                      )}
                      {bot.allowed && bot.httpStatus === 403 && (
                        <span
                          className='text-[10px] flex items-center gap-0.5 rounded-full px-1.5 py-0.5'
                          style={{
                            backgroundColor: '#f9731615',
                            color: '#f97316',
                          }}
                          title='Policy allows this bot, but the server returned 403. Likely WAF/CDN (e.g. Cloudflare) blocking our test request. Real bots from verified IP ranges are typically whitelisted.'
                        >
                          <Shield size={9} />
                          WAF
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
